// Admin utilities for role checking and permissions
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase/firebase-config';

// Cache for admin status to avoid repeated database calls
const adminStatusCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Check if a user has admin privileges
 * @param {string} userId - The user ID to check
 * @param {Object} userProfile - Optional user profile data to avoid extra DB call
 * @returns {Promise<boolean>} - True if user is admin, false otherwise
 */
export const isAdmin = async (userId, userProfile = null) => {
  if (!userId) {
    return false;
  }

  // Check cache first
  const cacheKey = userId;
  const cached = adminStatusCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.isAdmin;
  }

  try {
    let profile = userProfile;
    
    // If profile not provided, fetch from database
    if (!profile) {
      const userProfileRef = doc(db, 'user_profiles', userId);
      const userProfileDoc = await getDoc(userProfileRef);
      
      if (!userProfileDoc.exists()) {
        // Cache negative result
        adminStatusCache.set(cacheKey, {
          isAdmin: false,
          timestamp: Date.now()
        });
        return false;
      }
      
      profile = userProfileDoc.data();
    }

    // Check admin status from profile
    const adminStatus = profile.role === 'admin' || profile.isAdmin === true;
    
    // Cache the result
    adminStatusCache.set(cacheKey, {
      isAdmin: adminStatus,
      timestamp: Date.now()
    });

    return adminStatus;
  } catch (error) {
    console.error('Error checking admin status:', error);
    
    // Cache negative result on error to prevent repeated failures
    adminStatusCache.set(cacheKey, {
      isAdmin: false,
      timestamp: Date.now()
    });
    
    return false;
  }
};

/**
 * Check if current user from auth context has admin privileges
 * @param {Object} user - Firebase auth user object
 * @param {Object} userProfile - User profile from context
 * @returns {Promise<boolean>} - True if user is admin, false otherwise
 */
export const isCurrentUserAdmin = async (user, userProfile = null) => {
  if (!user?.uid) {
    return false;
  }

  return await isAdmin(user.uid, userProfile);
};

/**
 * Clear admin status cache for a specific user or all users
 * @param {string} userId - Optional user ID to clear, if not provided clears all
 */
export const clearAdminCache = (userId = null) => {
  if (userId) {
    adminStatusCache.delete(userId);
  } else {
    adminStatusCache.clear();
  }
};

/**
 * Batch check admin status for multiple users
 * @param {string[]} userIds - Array of user IDs to check
 * @returns {Promise<Object>} - Object mapping userId to admin status
 */
export const batchCheckAdminStatus = async (userIds) => {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    return {};
  }

  const results = {};
  const uncachedUserIds = [];

  // Check cache first
  for (const userId of userIds) {
    const cached = adminStatusCache.get(userId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      results[userId] = cached.isAdmin;
    } else {
      uncachedUserIds.push(userId);
    }
  }

  // Fetch uncached users
  if (uncachedUserIds.length > 0) {
    try {
      // Note: In a real implementation, you might want to use a batch get
      // For now, we'll check each user individually
      const promises = uncachedUserIds.map(async (userId) => {
        const adminStatus = await isAdmin(userId);
        return { userId, adminStatus };
      });

      const uncachedResults = await Promise.all(promises);
      
      for (const { userId, adminStatus } of uncachedResults) {
        results[userId] = adminStatus;
      }
    } catch (error) {
      console.error('Error in batch admin check:', error);
      
      // Set remaining users to false on error
      for (const userId of uncachedUserIds) {
        if (!(userId in results)) {
          results[userId] = false;
        }
      }
    }
  }

  return results;
};

/**
 * Hook-like function to get admin status with caching
 * This can be used in components that need to check admin status
 * @param {string} userId - User ID to check
 * @param {Object} userProfile - Optional user profile
 * @returns {Promise<{isAdmin: boolean, loading: boolean, error: string|null}>}
 */
export const getAdminStatus = async (userId, userProfile = null) => {
  if (!userId) {
    return {
      isAdmin: false,
      loading: false,
      error: 'User ID is required'
    };
  }

  try {
    // Clear cache to force fresh check for this specific function
    const cacheKey = userId;
    const cached = adminStatusCache.get(cacheKey);
    
    // If we have a cached error or need to force a fresh check, clear it
    if (cached && cached.error) {
      adminStatusCache.delete(cacheKey);
    }

    const adminStatus = await isAdmin(userId, userProfile);
    return {
      isAdmin: adminStatus,
      loading: false,
      error: null
    };
  } catch (error) {
    return {
      isAdmin: false,
      loading: false,
      error: error.message
    };
  }
};

/**
 * Utility to check if user can perform admin actions
 * This includes additional checks beyond just admin role
 * @param {string} userId - User ID to check
 * @param {Object} userProfile - Optional user profile
 * @returns {Promise<boolean>} - True if user can perform admin actions
 */
export const canPerformAdminActions = async (userId, userProfile = null) => {
  if (!userId) {
    return false;
  }

  try {
    const adminStatus = await isAdmin(userId, userProfile);
    
    // Additional checks can be added here
    // For example: account status, permissions, etc.
    
    return adminStatus;
  } catch (error) {
    console.error('Error checking admin action permissions:', error);
    return false;
  }
};

/**
 * Check if an email belongs to an admin user
 * This is a simplified check that assumes admin emails are stored in user profiles
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} - True if email belongs to admin user
 */
export const isAdminEmail = async (email) => {
  if (!email) {
    return false;
  }

  try {
    // This is a placeholder implementation
    // In a real app, you might query users by email to check admin status
    // For now, we'll check against a list of known admin emails
    const adminEmails = [
      'admin@burnt.com',
      'grey@burnt.com',
      // Add other admin emails as needed
    ];
    
    return adminEmails.includes(email.toLowerCase());
  } catch (error) {
    console.error('Error checking admin email:', error);
    return false;
  }
};

// Export cache management functions for testing
export const __testing__ = {
  adminStatusCache,
  CACHE_DURATION
};